import { View } from 'backbone';
import _ from 'underscore';
import ButtonHtml from '../html/genericButton.html';
import Environment from './environment';
import { getLogger } from './logging/logger';
import AudioManager, { SOUNDS } from './manager/audioManager';
import gsap, { BUTTON_EFFECTS } from './util/buttonGSAPEffects';

/**
 *
 * Note: Touch event support was removed from this component.
 * If touch event functionality is needed, refer to commit 756cf033f3484776e587c4cb91c9917e8a982cfe
 * for the previous implementation.
 *
 */

const logger = getLogger('GenericButton');

/**
 * @typedef {object} GenericButtonOptions
 * @property {string} [id] Unique identifier of button
 * @property {string} [type] A type for the button?
 * @property {string[]} [text] Array of strings to be used as lines
 * @property {string} [className] Space seperated class names to be applied to the button
 * @property {string} [image] - Url to the image to be used
 * @property {string} [tagName] - The HTML tag type to use on the element
 * @property {string} [dataId] - A data attribute with the name 'id' to be added to the HTML element
 * @property {string} [route] - Used for anchor based buttons to navigate to a specific page in the application
 * @property {string} [uri] - Used for anchor based buttons to navigate to a specific URL
 * @property {string} [name] - A line of text to be displayed on the button
 * @property {string} [title] - A line of text to be displayed on the button
 * @property {string} [subtitle] - A line of text to be displayed on the button
 * */

const ATTRIBUTES = Object.freeze({
	id: 'id',
	type: 'type',
	text: 'text',
	className: 'className',
	image: 'image',
	tagName: 'tagName',
	dataId: 'dataId',
	route: 'route',
	uri: 'uri',
	name: 'name',
	title: 'title',
	subtitle: 'subtitle',
});
const TAG_NAMES = Object.freeze({
	anchor: 'a',
	button: 'button',
});
const EVENTS = Object.freeze({
	click: 'click',
	focus: 'focusin mouseover',
	unfocus: 'focusout mouseout',
	down: 'keydown mousedown',
	up: 'keyup mouseup',
	incorrect: 'incorrect',
});

const DEFAULT_CLASSES = 'btn selectable';

export { ATTRIBUTES, BUTTON_EFFECTS, EVENTS };

export default class GenericButton extends View {
	className() {
		return DEFAULT_CLASSES;
	}

	events() {
		return {
			click: this.onClick,

			focusin: this.onFocus,
			mouseover: this.onFocus,

			focusout: this.onUnfocus,
			mouseout: this.onUnfocus,

			keydown: this.onPressDown,
			mousedown: this.onPressDown,

			keyup: this.onRelease,
			mouseup: this.onRelease,

			incorrect: this.onIncorrect,
		};
	}

	get template() {
		return _.template(ButtonHtml);
	}

	/**
	 * Checks the button data for a route or URI to determine if an HTML Anchor or Button tag should be used.
	 *
	 * @param {GenericButtonOptions} options Data used to populate the button
	 */
	preinitialize(options) {
		logger.log(`[${this.preinitialize.name}]`, { options });

		// Determine tagName based on route and uri from options
		const { tagName = '', route = '', uri = '' } = options;
		if (!tagName) {
			this.tagName = route || uri ? TAG_NAMES.anchor : TAG_NAMES.button;
		}
	}

	initialize(options) {
		logger.log(`[${this.initialize.name}]`, { options });

		// **************** DATA FIELDS **************** //
		/** @type {string} Unique identifier of button */
		this[ATTRIBUTES.id] = undefined;
		/** @type {string} The type of button */
		this[ATTRIBUTES.type] = 'button';
		/** @type {string} Generic field for engines to tap into */
		this[ATTRIBUTES.dataId] = undefined;

		// **************** NAVIGATIONAL FIELDS **************** //
		/** @type {string} Route that maps to internal page */
		this[ATTRIBUTES.route] = undefined;
		/** @type {string} For deeplinking */
		this[ATTRIBUTES.uri] = undefined;

		// **************** VISUAL FIELDS **************** //
		/** @type {string[]} An array of string(s) for multi-line support */
		this[ATTRIBUTES.text] = [];
		/** @type {string} Line of text. // ! Deprecated Soon. */
		this[ATTRIBUTES.name] = undefined;
		/** @type {string} Line of text. // ! Deprecated Soon. */
		this[ATTRIBUTES.title] = undefined;
		/** @type {string} Line of text. // ! Deprecated Soon. */
		this[ATTRIBUTES.subtitle] = undefined;
		/** @type {string} Path/Url of an image to embed */
		this[ATTRIBUTES.image] = undefined;

		// **************** RUNTIME ATTRIBTUES **************** //
		/** @type {string} */
		this.href = undefined;
		/** @type {Boolean} Whether or not instance is in the "down" state of a click */
		this.isPressedDown = false;
		/** @type {GSAPTimeline} */
		this.timeline = gsap.timeline();
		/** @type {string} This dictates which enter to listen to (keyboard-enter) */
		this.typeOfPressDown = undefined;

		Object.assign(this, options);

		if (this[ATTRIBUTES.dataId] !== undefined) {
			this.$el.attr('data-id', this[ATTRIBUTES.dataId]);
		}
		this.$el.addClass(DEFAULT_CLASSES);

		this.render(options);
	}

	render(btnData) {
		logger.log(`[${this.render.name}]`, { btnData });

		const templateData = this.getButtonData(btnData);
		const { href, type } = templateData;

		// If href is set, this is an anchor tag.
		if (href) {
			this.$el.attr({ href });
		} else {
			// Otherwise, this is a true <button>.
			this.$el.attr({ type });
		}
		this.$el.append(this.template(templateData));

		return this;
	}

	onPressDown(event) {
		logger.log(`[${this.onPressDown.name}]`, { event, isDown: this.isPressedDown });

		if (this.isPressedDown || !this.isValidPressDown(event)) {
			return;
		}

		this.isPressedDown = true;
		this.timeline[BUTTON_EFFECTS.down](this.el).then(() => this.$el.addClass('pressedDown'));
	}

	isValidPressDown(event) {
		const { type, code, which: pointerCode } = event;
		logger.log(`[${this.isValidPressDown.name}]`, { type, code, pointerCode, event });

		if (this.typeOfPressDown) {
			// Press has already been initiated
			return false;
		}

		const isKeyboardEnter = type === 'keydown' && code === 'Enter';
		const isMouseDown = type === 'mousedown' && pointerCode === 1; // checks mouse event and checks for left button

		if (isKeyboardEnter || isMouseDown) {
			this.typeOfPressDown = type;
			return true;
		}

		return false;
	}

	/**
	 * Handle click
	 * @param {Event} event DOM event
	 */
	onClick(event) {
		logger.debug(`[${this.onClick.name}]`, { event, isPressedDown: this.isPressedDown });

		if (this.isPressedDown) {
			event.preventDefault();
			event.stopImmediatePropagation();
		}
	}

	onRelease(event) {
		logger.debug(`[${this.onRelease.name}]`, { event, isPressedDown: this.isPressedDown });

		if (!this.isPressedDown || !this.isValidRelease(event)) {
			event.preventDefault();
			return;
		}

		// Visually being released. Remove class immediately.
		this.$el.removeClass('pressedDown');

		this.isPressedDown = false;
		this.timeline[BUTTON_EFFECTS.up](this.el).then(() => {
			this.typeOfPressDown = undefined;
		});

		/**
		 * 'down', 'click', and 'up' type events happen in a different order depending on
		 * if they were triggered by a mouse or keyboard event. We need to trigger 'click'
		 * for keyboard input here.
		 */
		if (event.type !== 'mouseup') {
			this.el.click();
		}

		if (!Environment.isMTheory()) {
			AudioManager.playSound(SOUNDS.buttonSelect);
		}
	}

	isValidRelease(event) {
		const { type, code, which: pointerCode } = event;
		logger.debug(`[${this.isValidRelease.name}]`, { type, code, pointerCode, event });

		if (!this.typeOfPressDown) {
			// No press down was initiated
			return false;
		}

		const isKeyboardEnter = type === 'keyup' && code === 'Enter';
		const isMouseUp = type === 'mouseup' && pointerCode === 1; // checks mouse event and checks for left button

		const matchingRelease =
			(isKeyboardEnter && this.typeOfPressDown === 'keydown') ||
			(isMouseUp && this.typeOfPressDown === 'mousedown');

		if (matchingRelease) {
			this.typeOfPressDown = undefined;
			return true;
		}

		return false;
	}

	onFocus(event) {
		logger.debug(`[${this.onFocus.name}]`, { event });

		AudioManager.playSound(SOUNDS.buttonChange);
		this.timeline[BUTTON_EFFECTS.focus](this.el);
	}

	onUnfocus(event) {
		logger.debug(`[${this.onUnfocus.name}]`, { event });

		if (this.isPressedDown) {
			this.timeline[BUTTON_EFFECTS.up](this.el);
			this.isPressedDown = false;
			this.typeOfPressDown = undefined;
		}

		this.timeline.add(gsap.effects[BUTTON_EFFECTS.focus](this.el).reverse());
	}

	onIncorrect(event) {
		logger.log(`[${this.onIncorrect.name}]`, { event });

		this.timeline[BUTTON_EFFECTS.incorrect](this.el);
	}

	/**
	 * Receives arbitrary data and returns the config needed for genericButton.html
	 * Assumes caller has provided it's necessary button properties.
	 *
	 * @param {Object} btnData contains generic data for a button
	 * @returns {Object} with needed properties to drop into template
	 */
	getButtonData(btnData) {
		logger.log(`[${this.getButtonData.name}]`, { btnData });

		if (!btnData) {
			logger.warn(`[${this.getButtonData.name}] Failed to make a button:`, { btnData });
		}

		const { [ATTRIBUTES.uri]: uri, [ATTRIBUTES.route]: route = '' } = btnData;
		// href will be either a uri or a route (and a route to the main page if neither exists)
		const href = uri || `#${route}`;

		return {
			...btnData,
			href,
			[ATTRIBUTES.text]:
				btnData[ATTRIBUTES.text] ||
				[
					btnData[ATTRIBUTES.name],
					btnData[ATTRIBUTES.title],
					btnData[ATTRIBUTES.subtitle],
				].filter((line) => line !== undefined),
		};
	}
}

/**
 * Creates and returns Generic buttons.
 *
 * @param {GenericButtonOptions | GenericButtonOptions[]} btnData Array or Object filled with arbitrary button data
 * @returns {Element[]} Instance(s) to easily add to DOM
 */
export function createButtons(btnData) {
	logger.log(`[${createButtons.name}]`, { btnData });

	if (!Array.isArray(btnData)) {
		btnData = [btnData];
	}

	return btnData.map((data) => new GenericButton(data).el);
}
